import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDebounce } from 'site-modules/shared/hooks/use-debounce';

import './scroll-down-arrow.scss';

export function ScrollDownArrow({ scrollArrowStrategy, displayable = false }) {
  const [showArrow, setShowArrow] = useState(false);
  const [listenScroll, setListenScroll] = useState(false);
  const resizeObserverRef = useRef(null);
  const resizeObserverTargetRef = useRef(null);

  const checkScrollArrow = useDebounce(() => {
    const shouldShow = scrollArrowStrategy.shouldShowArrow();
    setShowArrow(shouldShow);

    if (shouldShow) {
      setListenScroll(true);
    }
  }, 100);

  const scrollDown = useCallback(() => {
    scrollArrowStrategy.scrollDown();
  }, [scrollArrowStrategy]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      resizeObserverTargetRef.current = null;
    },
    [checkScrollArrow, scrollArrowStrategy]
  );

  useEffect(() => {
    if (!displayable) {
      return;
    }

    const resizeObserverTarget = scrollArrowStrategy.getResizeObserverTarget();

    if (!resizeObserverTarget) {
      return;
    }

    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(checkScrollArrow);
    }

    if (resizeObserverTargetRef.current !== resizeObserverTarget) {
      if (resizeObserverTargetRef.current) {
        resizeObserverRef.current.unobserve(resizeObserverTargetRef.current);
      }

      resizeObserverRef.current.observe(resizeObserverTarget);
      resizeObserverTargetRef.current = resizeObserverTarget;
    }
  }, [checkScrollArrow, scrollArrowStrategy, displayable]);

  useEffect(() => {
    if (listenScroll) {
      scrollArrowStrategy.attachScrollListener(checkScrollArrow);
    }

    return () => {
      scrollArrowStrategy.detachScrollListener(checkScrollArrow);
    };
  }, [checkScrollArrow, listenScroll, scrollArrowStrategy]);

  return (
    <div className={classnames('scroll-arrow-wrapper fade pos-r', { show: showArrow })}>
      <button onClick={scrollDown} className="scroll-arrow bg-cool-gray-20 text-white border-0 pos-a center-x">
        <span className="visually-hidden">Scroll down</span>
        <i className="icon-arrow-down8 size-16 pos-a center-xy" aria-hidden />
      </button>
    </div>
  );
}

ScrollDownArrow.propTypes = {
  scrollArrowStrategy: PropTypes.shape({
    shouldShowArrow: PropTypes.func.isRequired,
    scrollDown: PropTypes.func.isRequired,
    getScrollContainer: PropTypes.func.isRequired,
    getResizeObserverTarget: PropTypes.func.isRequired,
    attachScrollListener: PropTypes.func.isRequired,
    detachScrollListener: PropTypes.func.isRequired,
  }).isRequired,
  displayable: PropTypes.bool,
};
